import { useState, useEffect } from 'react';
import { Search, Bell, Send, RefreshCw, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { ContentRecord, PublishStatusSummary } from '../../types/content';
import { MetricCard, EmptyState } from '../shared';

const AI_SYSTEMS: Array<'chatgpt' | 'perplexity' | 'gemini' | 'claude' | 'copilot'> = [
  'chatgpt',
  'perplexity',
  'gemini',
  'claude',
  'copilot'
];

export function PublishToAI() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<ContentRecord[]>([]);
  const [publishStats, setPublishStats] = useState<PublishStatusSummary[]>([]);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'contentRecords'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      const recordsData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as ContentRecord);
      setRecords(recordsData);

      // Calculate publish stats for each AI system
      const stats: PublishStatusSummary[] = AI_SYSTEMS.map(system => {
        const published = recordsData.filter(r => r.publishedToAI[system] === 'published').length;
        const pending = recordsData.filter(r => r.publishedToAI[system] === 'pending').length;
        const failed = recordsData.filter(r => r.publishedToAI[system] === 'failed').length;
        const notPublished = recordsData.filter(r => r.publishedToAI[system] === 'not_published').length;

        return {
          aiSystem: system,
          published,
          pending,
          failed,
          notPublished,
          lastPublished: undefined // Would come from a publishLog collection
        };
      });

      setPublishStats(stats);

    } catch (error) {
      console.error('Error loading publish data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalRecords = records.length;
  const readyToPublish = records.filter(r =>
    r.status === 'approved' && (r.aiReadiness === 'excellent' || r.aiReadiness === 'good')
  ).length;
  const publishedAcrossAll = records.filter(r =>
    AI_SYSTEMS.every(sys => r.publishedToAI[sys] === 'published')
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t2-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Content</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Publish to AI</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t2-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t2-primary uppercase tracking-wide mb-1">Kodiac Content</p>
          <h1 className="text-3xl font-semibold text-gray-900">Publish to AI</h1>
          <p className="text-sm text-gray-600 mt-1">Distribute content across AI platforms and knowledge bases</p>
        </div>

        {/* 3-Stat Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <MetricCard
            label="Total Records"
            value={totalRecords.toString()}
          />
          <MetricCard
            label="Ready to Publish"
            value={readyToPublish.toString()}
          />
          <MetricCard
            label="Published Everywhere"
            value={publishedAcrossAll.toString()}
          />
        </div>

        {/* AI System Publish Status */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base font-semibold text-gray-900">AI Platform Status</h2>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 border border-t2-primary text-t2-primary rounded-lg hover:bg-t2-primary hover:text-white text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {publishStats.length === 0 ? (
            <EmptyState
              icon={Send}
              title="No publish data"
              description="Sync content to start publishing to AI platforms"
            />
          ) : (
            <div className="space-y-4">
              {publishStats.map(stat => {
                const total = stat.published + stat.pending + stat.failed + stat.notPublished;
                const completionPercent = total > 0 ? Math.round((stat.published / total) * 100) : 0;

                return (
                  <div key={stat.aiSystem} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 capitalize mb-1">
                          {stat.aiSystem === 'chatgpt' ? 'ChatGPT' : stat.aiSystem.charAt(0).toUpperCase() + stat.aiSystem.slice(1)}
                        </h3>
                        {stat.lastPublished && (
                          <p className="text-xs text-gray-600">
                            Last published: {stat.lastPublished.toDate().toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-t2-primary">{completionPercent}%</div>
                        <div className="text-xs text-gray-500">complete</div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-3 flex overflow-hidden mb-3">
                      <div
                        className="bg-success-green h-3"
                        style={{ width: `${(stat.published / total) * 100}%` }}
                        title={`${stat.published} published`}
                      />
                      <div
                        className="bg-blue-400 h-3"
                        style={{ width: `${(stat.pending / total) * 100}%` }}
                        title={`${stat.pending} pending`}
                      />
                      <div
                        className="bg-critical-red h-3"
                        style={{ width: `${(stat.failed / total) * 100}%` }}
                        title={`${stat.failed} failed`}
                      />
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="flex items-center justify-center gap-1 text-success-green mb-1">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-lg font-semibold">{stat.published}</span>
                        </div>
                        <div className="text-xs text-gray-600">Published</div>
                      </div>
                      <div>
                        <div className="flex items-center justify-center gap-1 text-blue-400 mb-1">
                          <Clock className="w-4 h-4" />
                          <span className="text-lg font-semibold">{stat.pending}</span>
                        </div>
                        <div className="text-xs text-gray-600">Pending</div>
                      </div>
                      <div>
                        <div className="flex items-center justify-center gap-1 text-critical-red mb-1">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-lg font-semibold">{stat.failed}</span>
                        </div>
                        <div className="text-xs text-gray-600">Failed</div>
                      </div>
                      <div>
                        <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
                          <span className="text-lg font-semibold">{stat.notPublished}</span>
                        </div>
                        <div className="text-xs text-gray-600">Not Published</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-4 flex items-center gap-2">
                      {stat.notPublished > 0 && (
                        <button className="flex-1 px-4 py-2 bg-t2-primary text-white rounded-lg hover:bg-t2-dark text-sm">
                          Publish {stat.notPublished} Records
                        </button>
                      )}
                      {stat.failed > 0 && (
                        <button className="flex-1 px-4 py-2 border border-critical-red text-critical-red rounded-lg hover:bg-red-50 text-sm">
                          Retry {stat.failed} Failed
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Publish Queue (if needed) */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Publishing Information</h3>
          <ul className="space-y-1 text-sm text-blue-800">
            <li>• Publishing to AI platforms happens automatically for approved content</li>
            <li>• Records must have AI readiness score &gt; 70 to publish</li>
            <li>• Failed publishes will retry automatically every 6 hours</li>
            <li>• Enterprise users can customize publish rules and schedules</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
